// src/Pages/ProductPage/ProductPage.jsx

import { useBooking } from "../../context/BookingContext"; // 🔥 Haetaan päivämäärät BookingContextista
import RentalTimeBar from "../../components/RentalTimeBar/RentalTimeBar";
import RelatedProducts from "../../components/RelatedProducts/RelatedProducts";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MdArrowBack,
  MdAddShoppingCart,
  MdRemoveShoppingCart,
  MdCalendarToday,
} from "react-icons/md";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import { adaptRentalProduct } from "../../utils/rentalProductAdapter";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton"; // polku tarpeen mukaan
import { trackEvent } from "../../utils/analytics";

// 🔥 Tuodaan TrailerCalendar (nimi pysyy WeeklyTrailerCalendar, jos haluat)
// mutta oletus nimen mukaan:
import TrailerCalendar from "../../components/TrailerCalendar/TrailerCalendar";

// 🔧 API-base tuotteille (Node/Express)
// - Devissä voit asettaa .env: REACT_APP_RENTAL_API_BASE=http://localhost:PORT
// - Prod: api-vuokraus.spiderlegsproductions.com (ei spiderlegsproductions.com/api)
const RENTAL_API_BASE =
  process.env.REACT_APP_RENTAL_API_BASE || "https://spiderlegsproductions.com";

const pageShellClass =
  "relative w-full overflow-hidden pt-24 pb-20 bg-gradient-to-br from-white via-[#F3FFFC] to-[#E9FBF7] text-[#111827] dark:from-[#050505] dark:via-[#08080A] dark:to-[#0D0D12] dark:text-white";

const productCardClass =
  "rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8";

const productTextClass =
  "product-description font-customFont1 text-base leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-lg [&_strong]:font-extrabold [&_strong]:!text-[#111827] dark:[&_strong]:!text-white";

const productTitleClass =
  "font-customFont2 text-4xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-5xl lg:text-6xl";

const productPriceClass =
  "font-customFont1 text-3xl font-extrabold leading-tight !text-[#111827] dark:!text-white sm:text-4xl";

const productImageShellClass =
  "relative flex w-full items-center justify-center overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/80 p-4 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95";

// Haetaan ostoskori localStoragesta
function getCart() {
  return JSON.parse(localStorage.getItem("rentalCart")) || [];
}

function getProductQuantityInCart(slug) {
  return getCart()
    .filter((item) => item.slug === slug)
    .reduce((sum, item) => sum + item.quantity, 0);
}

const calculateBillableDays = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.max(
    Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1,
    1
  );
};

// Tarkistaa, menevätkö kaksi aikaväliä päällekkäin
function isOverlap(start1, end1, start2, end2) {
  return start1 <= end2 && start2 <= end1;
}

const ProductPage = () => {
  const { selectedDates, setSelectedDates } = useBooking();

  const hasRentalDates =
    Boolean(selectedDates?.startDate && selectedDates?.endDate);

  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔥 Avuksi lista peräkärry-kriteereistä
  const trailerKeywords = [
    "peräkärry",
    "perakarry",
    "traileri",
    "hirvi 350",
    "kuomukärry",
    "kärry",
    "karry",
  ];

  // Kamerapakettien state
  const [selectedPackage, setSelectedPackage] = useState("");

  // Tuotteen tiedot ⚠️ TÄMÄ TÄYTYY OLLA ENNEN packages-laskentaa
  const [product, setProduct] = useState(null);

  const lastSentDatesRef = useRef(null);

  // 🔄 Nollataan päivämäärä-eventin muisti, kun tuote vaihtuu
  useEffect(() => {
    lastSentDatesRef.current = null;
  }, [product?.id]);

  // 📸 Instax Mini 12 – staattiset vuokrapaketit
  const INSTAX_PACKAGES = [
    { id: 1, name: "Peruspaketti", price: 80, film_count: 20 },
    { id: 2, name: "Keskipaketti", price: 140, film_count: 50 },
    { id: 3, name: "Premium-paketti", price: 200, film_count: 100 },
  ];

  const packages =
    product?.slug === "fujifilm-instax-mini-12"
      ? INSTAX_PACKAGES
      : [];

  // Onko tuote peräkärry?
  const isTrailer =
    product &&
    trailerKeywords.some(
      (keyword) =>
        product.name?.toLowerCase().includes(keyword) ||
        product.slug?.toLowerCase().includes(keyword)
    );

  const [activeImg, setActiveImg] = useState(null);

  // ⏱️ Laskutettavat vuorokaudet (24 h -jaksot)
  const [billableDays, setBillableDays] = useState(1);

  // 🔽 Pieni päivänvalitsin RentalTimeBarin alla
  const [showDayPanel, setShowDayPanel] = useState(false);

  // Saatavuus & varaukset
  const [, setAvailable] = useState(0);
  const [reservations, setReservations] = useState([]);

  // Onko tuote korissa?
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // Onko tuote täysin varattu
  const [isBooked, setIsBooked] = useState(false);

  // Montako on varattu
  const [bookedCount, setBookedCount] = useState(0);

  // Määrän valinta – näytetään vain, jos kyseessä ei ole kameratuote
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  // Jos sivulle tullaan /tuote/slug#kalenteri -> scrollaa kalenteriin
  useEffect(() => {
    if (location.hash === "#kalenteri") {
      const interval = setInterval(() => {
        const element = document.getElementById("kalenteri");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [location, product]);

  // ✅ UUSI – Node API
  useEffect(() => {
    if (!product?.id) return;

    const fetchReservations = async () => {
      try {
        const res = await fetch(
          `${RENTAL_API_BASE}/api/rentals/reservations?productId=${product.id}`
        );

        if (!res.ok) {
          console.error("Reservation fetch failed:", res.status);
          return;
        }

        const data = await res.json();

        if (data.ok) {
          setReservations(data.reservations || []);
        } else {
          console.error("Reservation API error:", data.error);
        }
      } catch (err) {
        console.error("Reservation fetch error:", err);
      }
    };

    fetchReservations();
  }, [product?.id, isTrailer, isBooked]);

  // 2) Tarkistetaan, onko tuote täysin varattu
  const checkIfProductIsFullyBooked = useCallback(
    (foundItem) => {
      // 🔐 Tarkista, että kaikki tarvittavat arvot on saatavilla ennen jatkamista
      if (!foundItem || !selectedDates?.startDate || !selectedDates?.endDate) {
        return;
      }

      // ✅ Stock: ensisijaisesti rivistä (foundItem), fallback productista
      const stock =
        Number(foundItem?.stock) > 0
          ? Number(foundItem.stock)
          : Number(product?.stock) > 0
            ? Number(product.stock)
            : 1;

      let totalBooked = 0;

      reservations.forEach((res) => {
        if (res.start && res.end) {
          const s = new Date(selectedDates.startDate);
          const e = new Date(selectedDates.endDate);
          const rStart = new Date(res.start);
          const rEnd = new Date(res.end);

          if (isOverlap(s, e, rStart, rEnd)) {
            // Node API: yksi rivi = yksi varaus tälle tuotteelle
            totalBooked += 1;
          }
        }
      });

      setBookedCount(totalBooked);
      setIsBooked(totalBooked >= stock);
    },
    [reservations, selectedDates, product]
  );

  // Pieni apufunktio automaattisen metakuvauksen generointiin
  const generateProductMetaDescription = (product) => {
    if (!product || !product.name) {
      return "Tutustu vuokrattaviin tuotteisiin – Spiderlegs Productions tarjoaa laadukkaat laitteet juhliin ja tapahtumiin.";
    }

    const name = product.name;
    const category = product.category?.toLowerCase() || "";
    const price = parseFloat(product.price);
    const formattedPrice = isNaN(price) ? "" : `Alkaen ${price}€/vrk. `;

    let base = "";

    if (category.includes("kaiutin") || name.toLowerCase().includes("kaiutin")) {
      base = `Täydellinen äänentoistoratkaisu juhliin, keikoille ja puheisiin – ${name}.`;
    } else if (
      category.includes("kamera") ||
      name.toLowerCase().includes("instax") ||
      name.toLowerCase().includes("kamera")
    ) {
      base = `${name} tallentaa juhlatunnelman – loistava valinta tapahtumiin ja vuokrattavaksi.`;
    } else if (category.includes("valo") || name.toLowerCase().includes("valo")) {
      base = `${name} luo näyttävän valaistuksen juhliin ja tapahtumiin.`;
    } else {
      base = `Vuokraa ${name} helposti – erinomainen valinta tapahtumiin, juhliin ja keikoille.`;
    }

    const full = `${formattedPrice}${base}`.trim();
    return full.length > 160 ? `${full.slice(0, 157)}…` : full;
  };

  // SEO: dynaaminen title tuotetyypin mukaan
  const generateProductTitle = (product) => {
    if (!product?.name) {
      return "Vuokrattavat tuotteet | Spiderlegs Productions";
    }

    const name = product.name;
    const slug = product.slug?.toLowerCase() || "";
    const category = product.category?.toLowerCase() || "";

    // Peräkärry
    if (
      slug.includes("perakarry") ||
      slug.includes("peräkärry") ||
      slug.includes("hirvi")
    ) {
      return `${name} – Peräkärryn vuokraus Keravalla | Spiderlegs Productions`;
    }

    // Kaiuttimet
    if (category.includes("kaiutin") || slug.includes("kaiutin")) {
      return `${name} – Kaiuttimen vuokraus | Spiderlegs Productions`;
    }

    // Valot
    if (category.includes("valo") || slug.includes("valo")) {
      return `${name} – Valojen vuokraus | Spiderlegs Productions`;
    }

    // Kamerat
    if (category.includes("kamera") || slug.includes("instax")) {
      return `${name} – Kameran vuokraus | Spiderlegs Productions`;
    }

    // Fallback
    return `${name} – Vuokraus | Spiderlegs Productions`;
  };

  useEffect(() => {
    if (product) {
      console.log("PRODUCT IMAGES:", product.images);
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;

    const {
      id,
      slug,
      name,
      category,
      price,
      stock,
    } = product;

    trackEvent("tuote_katsottu", {
      tuote_id: id,
      tuote_slug: slug,
      tuote_nimi: name,
      kategoria: category || "",
      hinta_per_vrk: Number(price) || 0,
      onko_perakarry: isTrailer,
      varasto: stock || 1,
      onko_varattu: isBooked,
    });

    // Tämä event on semanttisesti "page view"
    // eikä saa laueta uudelleen reaktiivisten statejen muutoksista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    if (!selectedDates?.startDate || !selectedDates?.endDate || !product?.id) {
      return;
    }

    const currentKey = `${selectedDates.startDate}|${selectedDates.endDate}`;

    // ⛔ Estetään saman päivämääräparin toistuvat eventit
    if (lastSentDatesRef.current === currentKey) return;

    lastSentDatesRef.current = currentKey;

    trackEvent("vuokrauspaivat_valittu", {
      tuote_id: product.id,
      tuote_nimi: product.name,
      aloitus_pvm: selectedDates.startDate,
      lopetus_pvm: selectedDates.endDate,
      vuorokaudet: billableDays,
      onko_perakarry: isTrailer,
    });
  }, [
    selectedDates?.startDate,
    selectedDates?.endDate,
    product?.id,
    product?.name,
    billableDays,
    isTrailer,
  ]);

  // 3) Haetaan tuote + saatavuus
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const res = await fetch(
          `${RENTAL_API_BASE}/api/products/${slug}`
        );

        if (!res.ok) {
          toast.error("Tuotetta ei löytynyt");
          return;
        }

        const apiProduct = await res.json();
        const adaptedProduct = adaptRentalProduct(apiProduct);

        setProduct(adaptedProduct);

        // ✅ pääkuva adapterin kautta (oikea host + fallback)
        if (adaptedProduct.images?.length > 0) {
          setActiveImg(adaptedProduct.images[0].image_url);
        } else if (adaptedProduct.image) {
          setActiveImg(adaptedProduct.image);
        } else {
          setActiveImg("https://spiderlegsproductions.com/assets/fallback.jpg");
        }
      } catch (error) {
        console.error("⚠️ Virhe tuotetietojen haussa:", error);
        toast.error("Virhe tuotetietojen haussa");
      }
    };

    fetchProductData();
  }, [slug]);

  // 3b) Päivitä saatavuus aina kun tuote / varaukset / kori muuttuu
  useEffect(() => {
    if (!product) return;

    const cartQty = getProductQuantityInCart(slug);
    const stockNow = Number(product.stock) > 0 ? Number(product.stock) : 1;

    // available = paljonko voi vielä lisätä koriin tämän päivänäkymän kannalta
    // (bookedCount tulee checkIfProductIsFullyBooked:stä)
    const freeNow = Math.max(
      0,
      stockNow - (Number(bookedCount) + Number(cartQty))
    );

    setAvailable(freeNow);
    setIsAddedToCart(cartQty > 0);
  }, [product, slug, bookedCount]);

  // 4) Päivitetään saatavuus, jos ostoskoria muutetaan
  useEffect(() => {
    const handleCartUpdate = () => {
      const currentQuantity = getProductQuantityInCart(slug);
      setIsAddedToCart(currentQuantity > 0);

      // päivitetään varausstatus uudelleen nykyisen tuotteen perusteella
      if (product) {
        checkIfProductIsFullyBooked(product);
        const stockNow = Number(product.stock) > 0 ? Number(product.stock) : 1;
        const freeNow = Math.max(
          0,
          stockNow - (Number(bookedCount) + Number(currentQuantity))
        );
        setAvailable(freeNow);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [slug, product, checkIfProductIsFullyBooked, bookedCount]);

  // Apufunktio vain nimen käsittelyyn
  const generalizedName = (name) => {
    return name;
  };

  const addToCart = () => {
    if (!product) return;

    const cartItems = getCart();

    // 🚫 Peräkärry-yhteensopivuuslogiikka
    const trailerInCart = cartItems.some((item) => item.flags?.isTrailer);

    if (trailerInCart && !isTrailer) {
      toast.error("Et voi lisätä muita tuotteita, kun vuokrauskorissa on peräkärry.");
      return;
    }

    if (!trailerInCart && isTrailer && cartItems.length > 0) {
      toast.error("Et voi lisätä peräkärryä, kun vuokrauskorissa on muita tuotteita.");
      return;
    }

    // =========================
    // 🚚 PERÄKÄRRY – ERILLISLOGIIKKA
    // =========================
    if (isTrailer) {
      const trailerData = JSON.parse(
        localStorage.getItem("trailerBookingData")
      );

      if (
        !trailerData?.startDate ||
        !trailerData?.endDate ||
        !trailerData?.startTime ||
        !trailerData?.endTime
      ) {
        toast.error("Valitse peräkärryn vuokrausaika kalenterista.");
        return;
      }

      const newItem = {
        id: product.id,
        slug: product.slug,
        name: product.name,
        image: product.image,
        quantity: 1,

        rental: {
          startDate: trailerData.startDate,
          endDate: trailerData.endDate,
          startTime: trailerData.startTime,
          endTime: trailerData.endTime,
          billableHours: trailerData.billableHours,
        },

        price: 25,
        totalPrice: trailerData.billableHours * 25,

        flags: {
          isTrailer: true,
          requiresRentalTime: true,
        },
      };

      const updatedCart = cartItems.filter(
        (item) => !item.flags?.isTrailer
      );

      updatedCart.push(newItem);

      localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
      window.dispatchEvent(new Event("cartUpdated"));

      trackEvent("tuote_lisatty_koriin", {
        tuote_id: product.id,
        tuote_slug: product.slug,
        tuote_nimi: product.name,
        kategoria: product.category || "peräkärry",
        maara: 1,
        hinta_yksikko: 25,
        vuorokaudet: null,
        kokonaishinta: 25,
        onko_perakarry: true,
        onko_paketti: false,
      });

      toast.success("HIRVI 350 lisätty vuokrauskoriin.");
      setIsAddedToCart(true);
      return;
    }

    // =========================
    // 📦 NORMAALIT TUOTTEET (ei muutoksia)
    // =========================

    if (!selectedDates?.startDate || !selectedDates?.endDate) {
      toast.error("Valitse ensin vuokrauksen aloitus- ja lopetuspäivä.");
      return;
    }

    let productName = product.name;
    let isPackage = false;

    const unitPrice =
      product.slug === "fujifilm-instax-mini-12"
        ? (() => {
            if (!selectedPackage) {
              toast.error("Valitse ensin vuokrapaketti.");
              return null;
            }

            const chosenPackage = packages.find(
              (p) => p.id === Number(selectedPackage)
            );

            if (!chosenPackage) {
              toast.error("Valittu paketti ei löytynyt.");
              return null;
            }

            productName = `${product.name} (${chosenPackage.name})`;
            isPackage = true;
            return Number(chosenPackage.price);
          })()
        : Number(product.price) || 0;

    if (unitPrice === null) return;

    const days = billableDays;
    const qty = selectedQuantity;

    const totalPrice = unitPrice * days * qty;

    const newItem = {
      id: product.id,
      slug: product.slug,
      name: productName,
      image: product.image,

      quantity: qty,
      price: unitPrice,        // ✅ MiniCart lukee tämän
      totalPrice: totalPrice,  // ✅ MiniCart lukee tämän

      rental: {
        startDate: selectedDates.startDate,
        endDate: selectedDates.endDate,
        billableDays: days,
      },

      flags: {
        requiresRentalTime: true,
        isTrailer: false,
        isPackage,
      },
    };

    const updatedCart = cartItems.filter(
      (item) => item.slug !== product.slug
    );

    updatedCart.push(newItem);
    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));

    trackEvent("tuote_lisatty_koriin", {
      tuote_id: product.id,
      tuote_slug: product.slug,
      tuote_nimi: productName,
      kategoria: product.category || "",
      maara: qty,
      hinta_yksikko: unitPrice,
      vuorokaudet: days,
      kokonaishinta: unitPrice * days * qty,
      onko_perakarry: false,
      onko_paketti: isPackage,
    });

    toast.success(`${productName} lisätty vuokrauskoriin.`);
    setIsAddedToCart(true);
  };

  // 6b) Poista tuote korista
  const removeFromCart = () => {
    const qtyToRemove = selectedQuantity;
    const cartItems = getCart();
    const existingIndex = cartItems.findIndex((item) => item.slug === slug);

    if (existingIndex === -1) {
      toast.error("Tuotetta ei ole korissa.");
      return;
    }

    const stock = product.stock || 1;
    const productName = cartItems[existingIndex].name;
    const currentQuantity = cartItems[existingIndex].quantity;

    if (currentQuantity <= qtyToRemove) {
      // Poistetaan kokonaan korista
      cartItems.splice(existingIndex, 1);
      setIsAddedToCart(false);
      localStorage.setItem("rentalCart", JSON.stringify(cartItems));
      window.dispatchEvent(new Event("cartUpdated"));

      trackEvent("tuote_poistettu_korista", {
        tuote_id: product.id,
        tuote_slug: slug,
        tuote_nimi: productName,
        kategoria: product.category || "",
        poistettu_maara: currentQuantity,
        uusi_maara: 0,
        hinta_yksikko: product.price || 0,
        vuorokaudet: billableDays || null,
        poistettu_kokonaishinta:
          (product.price || 0) * (billableDays || 1) * currentQuantity,
        onko_perakarry: isTrailer,
      });

      if (stock === 1) {
        toast.info(`${productName} poistettu korista.`);
      } else {
        toast.info(`Kaikki ${generalizedName(productName)} on nyt poistettu korista!`);
      }
    } else {
      // Poistetaan vain osa
      cartItems[existingIndex].quantity -= qtyToRemove;

      const priceNum = parseFloat(cartItems[existingIndex].price) || 0;

      cartItems[existingIndex].totalPrice =
        cartItems[existingIndex].quantity * priceNum;

      localStorage.setItem("rentalCart", JSON.stringify(cartItems));
      window.dispatchEvent(new Event("cartUpdated"));

      trackEvent("tuote_poistettu_korista", {
        tuote_id: product.id,
        tuote_slug: slug,
        tuote_nimi: productName,
        kategoria: product.category || "",
        poistettu_maara: qtyToRemove,
        uusi_maara: cartItems[existingIndex].quantity,
        hinta_yksikko: product.price || 0,
        vuorokaudet: billableDays || null,
        poistettu_kokonaishinta:
          (product.price || 0) * (billableDays || 1) * qtyToRemove,
        onko_perakarry: isTrailer,
      });

      const newQuantity = currentQuantity - qtyToRemove;

      toast.info(
        `${qtyToRemove} kpl tuotetta ${productName} poistettu korista. Nyt korissa on yhteensä ${newQuantity} kpl.`
      );
    }
  };

  // Määrän valinta
  const incrementQuantity = () => {
    setSelectedQuantity((prev) =>
      prev < freeCountWithCart ? prev + 1 : prev
    );
  };

  const decrementQuantity = () => {
    setSelectedQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // Aktiivinen kuva klikkauksesta
  const handleImageClick = (imgUrl) => {
    setActiveImg(imgUrl);
  };

  // Jos tuote ei vielä ladattu, näytetään "Ladataan..."
  if (!product) {
    return (
      <main className={pageShellClass}>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={productCardClass}>
            <p className="font-customFont1 text-lg font-semibold !text-[#374151] dark:!text-[#D1D5DB]">
              Ladataan tuotetietoja...
            </p>
          </div>
        </div>
      </main>
    );
  }

  // Lasketaan varastomäärä
  const stock = product.stock || 1;

  // Montako jo korissa
  const cartQuantity = getProductQuantityInCart(slug);

  // Paljonko jäljellä käytettävissä
  const freeCountWithCart = Math.max(0, stock - (bookedCount + cartQuantity));

  // 🧮 RentalTimeBar – hinnan laskenta (ei peräkärrylle)
  const rentalBarPricePerDay = (() => {
    if (!product) return 0;

    // Instax käyttää valittua pakettia
    if (product.slug === "fujifilm-instax-mini-12" && selectedPackage) {
      const selected = packages.find(
        (p) => p.id === Number(selectedPackage)
      );

      return selected ? selected.price : 0;
    }

    return Number(product.price) || 0;
  })();

  // Luo SEO-kuvaus
  const metaDesc = generateProductMetaDescription(product);

  // 🖼️ OG-kuva kaikille tuotteille:
  // 1) /assets/og/{slug}.jpg
  // 2) tuotteen pääkuva
  // 3) geneerinen fallback
  const ogImage = product?.slug
    ? `https://spiderlegsproductions.com/assets/og/${product.slug}.jpg`
    : product?.images?.[0]?.image_url ||
      "https://spiderlegsproductions.com/assets/fallback.jpg";

  const productShareUrl = product?.slug
    ? `https://spiderlegsproductions.com/tuote/${product.slug}`
    : "https://spiderlegsproductions.com/vuokrattavat";

  // Renderöidään sivu
  return (
    <>
      <Helmet>
        {/* Title + meta description */}
        <title>{generateProductTitle(product)}</title>
        <meta name="description" content={metaDesc} />

        {/* Open Graph (EI canonicalia, EI og:urlia) */}
        <meta property="og:type" content="product" />
        <meta
          property="og:title"
          content={`Spiderlegs Productions | ${product.name}`}
        />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Spiderlegs Productions | ${product.name}`}
        />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <main className={pageShellClass}>
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute left-1/2 top-10 h-72 w-72
            -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
            dark:bg-primary/10
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-24 top-96 h-96 w-96
            rounded-full bg-[#30f2cf]/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ResponsiveButton
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }

              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }, 100);
            }}
            variant="backbutton"
            className="mb-8"
          >
            <div className="flex items-center gap-2">
              <MdArrowBack className="text-xl" />
              <span>Takaisin</span>
            </div>
          </ResponsiveButton>

          {/* Jos tuote on peräkärry, näytetään kuva, otsikko+kuvaus ja kalenteri vierekkäin */}
          {isTrailer ? (
            <div className="grid grid-cols-1 gap-10 pb-10 xl:grid-cols-[0.85fr_1.15fr] xl:items-start">
              {/* Vasen sarake: kuva + otsikko + kuvaus */}
              <div id="trailerinfo" className="flex min-w-0 w-full flex-col gap-6">
                {/* Tuotekuva */}
                {activeImg && (
                  <div className={`${productImageShellClass} min-h-[360px] sm:min-h-[420px] xl:min-h-[620px]`}>
                    <img
                      src={activeImg}
                      alt={product.name}
                      title={product.name}
                      className="h-full max-h-[620px] w-full object-contain drop-shadow-[0_18px_35px_rgba(0,0,0,0.28)]"
                    />
                  </div>
                )}

                {/* Tuotteen otsikko ja kuvaus */}
                <div className={`${productCardClass} flex flex-col gap-5`}>
                  <h1 className={productTitleClass}>
                    {product.name}
                  </h1>

                  <div
                    className={productTextClass}
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />

                  <div className="my-2">
                    <a
                      href="/palvelut#perakarryn-vuokraus"
                      className="
                        font-customFont1 text-base font-extrabold
                        !text-[#111827] underline decoration-primary decoration-2 underline-offset-4
                        transition hover:!text-primary
                        dark:!text-white dark:hover:!text-primary
                      "
                    >
                      📄 Lue vuokrausehdot ja käyttöohjeet
                    </a>
                  </div>

                  <div className={productPriceClass}>
                    {product.slug === "fujifilm-instax-mini-12"
                      ? (() => {
                          if (!selectedPackage) return "Valitse vuokrapaketti";

                          const selected = packages.find(
                            (p) => p.id === Number(selectedPackage)
                          );

                          if (!selected) return "—";

                          const total = selected.price * billableDays;

                          return `${total} €`;
                        })()
                      : `${product.price} € / vrk`}

                    {product.slug === "fujifilm-instax-mini-12" && selectedPackage && (
                      <p className="mt-1 font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                        {(() => {
                          const selected = packages.find(
                            (p) => p.id === Number(selectedPackage)
                          );

                          if (!selected) return null;

                          return `${selected.price} € / vrk × ${billableDays} vrk`;
                        })()}
                      </p>
                    )}

                    <span className="block pt-2 font-customFont1 text-base font-semibold !text-[#4B5563] dark:!text-[#D1D5DB] sm:text-lg">
                      Omavastuu vahinkotapauksissa 200 €
                    </span>
                  </div>
                </div>
              </div>

                <div
                  id="kalenteri"
                  className={`${productCardClass} min-w-0 scroll-mt-32 w-full`}
                >
                <TrailerCalendar reservations={reservations} />
              </div>
            </div>
          ) : (
            /* Muut tuotteet (ei-peräkärryt) saavat normaalin layoutin */
            <div className="grid grid-cols-1 gap-10 pb-10 lg:grid-cols-[1fr_0.95fr] lg:items-start">
              {/* Vasen sarake: Kuvat, pikkukuvat, videot */}
              <div className="flex flex-col gap-5">
                {activeImg && (
                  <div className="flex w-full justify-center">
                    <div className={`${productImageShellClass} min-h-[360px] sm:min-h-[430px] lg:min-h-[560px]`}>
                      {isBooked && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%) rotate(-15deg)",
                            backgroundColor: "red",
                            color: "white",
                            padding: "20px 50px",
                            fontSize: "45px",
                            fontWeight: "bold",
                            borderRadius: "10px",
                            textTransform: "uppercase",
                            zIndex: 100,
                            boxShadow: "10px 10px 30px rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            lineHeight: "1.2",
                            flexDirection: "column",
                            opacity: 0.85,
                          }}
                        >
                          Nyt VARATTU
                        </div>
                      )}

                      <img
                        src={activeImg}
                        alt={product.name}
                        title={product.name}
                        className="h-full max-h-[560px] w-full object-contain drop-shadow-[0_18px_35px_rgba(0,0,0,0.28)]"
                      />
                    </div>
                  </div>
                )}

                {/* Pikkukuvat */}
                {Array.isArray(product.images) && product.images.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-4">
                    {product.images.map((img) => (
                      <button
                        key={img.image_id}
                        onClick={() => handleImageClick(img.image_url)}
                        className="
                          h-24 w-24 cursor-pointer rounded-2xl border border-[#DDE7E5]
                          bg-white/80 p-2 shadow-sm transition
                          hover:-translate-y-0.5 hover:border-primary hover:shadow-md
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                          dark:border-darkborder dark:bg-[#111114]/95
                          sm:h-28 sm:w-28
                        "
                      >
                        <img
                          src={img.image_url}
                          alt={img.alt_text || product.name}
                          title={img.alt_text || product.name}
                          className="h-full w-full rounded-xl object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Videot, jos on */}
                {Array.isArray(product.videos) && product.videos.length > 0 && (
                  <>
                    {Array.isArray(product.videos) && product.videos.length > 0 && (
                      <div className={`${productCardClass} mt-5`}>
                        <h2 className="font-customFont2 text-center text-4xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                          Videot
                        </h2>

                        <div
                          className={`grid w-full gap-6 pt-8 ${
                            product.videos.length === 1
                              ? "place-items-center"
                              : "grid-cols-1 md:grid-cols-2"
                          }`}
                        >
                          {product.videos.map((vid) => (
                            <div
                              key={vid.video_id}
                              className={`relative w-full ${
                                product.videos.length === 1
                                  ? "max-w-xl"
                                  : "max-w-full"
                              }`}
                              style={{ paddingBottom: "56.25%", height: 0 }}
                            >
                              <iframe
                                className="absolute left-0 top-0 h-full w-full rounded-xl"
                                src={vid.video_url}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={vid.title || product.name}
                              ></iframe>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Oikea sarake: Tuotekuvaus ja ostoskori-napit */}
              <div
                id="trailerinfo"
                className={`${productCardClass} flex flex-col gap-5`}
              >
                <div>
                  {/* Otsikko */}
                  <div className="flex flex-row items-center">
                    <h1 className={productTitleClass}>
                      {product.name}
                    </h1>
                  </div>

                  {/* Tuotekuvaus (HTML) */}
                  <div
                    className={`${productTextClass} mt-6`}
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />

                  {/* Jako-napit */}
                  <div className="flex flex-row gap-2 py-6">
                    <FacebookShareButton url={productShareUrl}>
                      <FacebookIcon size={32} round />
                    </FacebookShareButton>

                    <TwitterShareButton url={productShareUrl}>
                      <TwitterIcon size={32} round />
                    </TwitterShareButton>

                    <WhatsappShareButton url={productShareUrl}>
                      <WhatsappIcon size={32} round />
                    </WhatsappShareButton>

                    <LinkedinShareButton url={productShareUrl}>
                      <LinkedinIcon size={32} round />
                    </LinkedinShareButton>

                    <EmailShareButton url={productShareUrl}>
                      <EmailIcon size={32} round />
                    </EmailShareButton>
                  </div>

                  {/* Hinta + omavastuu (vain jos trailer) */}
                  <div className="flex flex-col gap-2">
                    <h2 title="Hinta" className={productPriceClass}>
                      {product.price}€ / vrk
                    </h2>

                    {isTrailer && (
                      <span className="font-customFont1 text-base font-semibold !text-[#4B5563] dark:!text-[#D1D5DB] sm:text-lg">
                        Omavastuu vahinkotapauksissa 200 €
                      </span>
                    )}
                  </div>

                  {/* Saatavilla */}
                  <div className="mt-4 font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                    {(() => {
                      const stock = product.stock || 1;

                      if (stock > 1) {
                        return (
                          <p>
                            Saatavilla: {freeCountWithCart} / {stock}
                          </p>
                        );
                      }

                      return null;
                    })()}
                  </div>

                  {/* Määrän valinta (jos varastoa > 1, ei trailer eikä instax-kamera) */}
                  {!isTrailer &&
                    product.slug !== "fujifilm-instax-mini-12" &&
                    stock > 1 &&
                    !isBooked && (
                      <div className="mb-5 mt-5 flex items-center gap-3">
                        <button
                          onClick={decrementQuantity}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111827] font-bold text-primary transition hover:scale-105 dark:bg-white dark:text-[#111827]"
                        >
                          -
                        </button>

                        <span className="font-customFont1 text-base font-bold !text-[#111827] dark:!text-white">
                          {selectedQuantity}
                        </span>

                        <button
                          onClick={incrementQuantity}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111827] font-bold text-primary transition hover:scale-105 dark:bg-white dark:text-[#111827]"
                        >
                          +
                        </button>
                      </div>
                    )}

                  {/* Instax-paketit (jos on Instax-kamera) */}
                  {product.slug === "fujifilm-instax-mini-12" && (
                    <div className="mt-6 rounded-3xl border border-primary/30 bg-primary/10 p-5 dark:border-primary/30 dark:bg-primary/5">
                      <h2 className="font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-4xl">
                        Valitse vuokrapaketti:
                      </h2>

                      <select
                        className="mt-4 w-full rounded-full border border-[#DDE7E5] bg-white p-3 font-customFont1 text-left text-base font-semibold text-[#111827] dark:border-darkborder dark:bg-darksurface dark:text-white"
                        value={selectedPackage}
                        onChange={(e) => setSelectedPackage(e.target.value)}
                        disabled={!hasRentalDates}
                      >
                        <option value="" disabled hidden>
                          Valitse paketti
                        </option>

                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} – {pkg.price} € ({pkg.film_count} kpl kuvia)
                          </option>
                        ))}
                      </select>

                      {!hasRentalDates && (
                        <p className="mt-2 font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                          Valitse ensin vuokrauksen aloitus- ja lopetuspäivä nähdäksesi hinnan.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Ostoskori-napit (ei trailer) */}
                  {!isTrailer && (
                    <div className="grid w-full grid-cols-1 gap-4 pt-6 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3">
                      <ResponsiveButton
                        onClick={addToCart}
                        icon={MdAddShoppingCart}
                        disabled={isBooked || freeCountWithCart <= 0}
                        variant="addtocart"
                        className="h-10 w-full min-w-[140px] sm:h-11 sm:text-sm md:w-auto md:text-base lg:h-12"
                      >
                        Lisää
                      </ResponsiveButton>

                      {isAddedToCart && !isBooked && (
                        <ResponsiveButton
                          onClick={removeFromCart}
                          icon={MdRemoveShoppingCart}
                          variant="removefromcart"
                          className="h-10 w-full min-w-[140px] sm:h-11 sm:text-sm md:w-auto md:text-base lg:h-12"
                        >
                          Poista
                        </ResponsiveButton>
                      )}

                      <ResponsiveButton
                        disabled={isBooked}
                        icon={MdCalendarToday}
                        variant="makeabooking"
                        className="h-10 w-full min-w-[140px] sm:h-11 sm:text-sm md:w-auto md:text-base lg:h-12"
                        onClick={() => {
                          if (isBooked) return;

                          trackEvent("siirtyi_varaamaan", {
                            tuote_id: product.id,
                            tuote_slug: product.slug,
                            tuote_nimi: product.name,
                            kategoria: product.category || "",
                            maara: selectedQuantity,
                            vuorokaudet: billableDays || null,
                            arvioitu_kokonaishinta:
                              (Number(product.price) || 0) *
                              (billableDays || 1) *
                              selectedQuantity,
                            onko_perakarry: isTrailer,
                            onko_varattu: isBooked,
                          });

                          window.scrollTo({ top: 0, behavior: "smooth" });
                          navigate("/tee-varaus");
                        }}
                      >
                        Varaa
                      </ResponsiveButton>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {!isTrailer && (
        <RentalTimeBar
          startDate={selectedDates?.startDate}
          endDate={selectedDates?.endDate}
          billableDays={billableDays}
          pricePerDay={rentalBarPricePerDay}
          quantity={selectedQuantity}
          showPanel={showDayPanel}
          onTogglePanel={() =>
            setShowDayPanel((prev) => !prev)
          }

          // 🔒 Ainoa totuus = BookingContext
          onChangeDates={(dates) => {
            // 🔒 BookingContext antaa nyt jo ISO-aikaleimat
            // Ei enää mitään "T12:00:00" virityksiä

            if (!dates?.startDate || !dates?.endDate) {
              setSelectedDates(dates);
              return;
            }

            const start = new Date(dates.startDate);
            const end = new Date(dates.endDate);

            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
              console.error("Invalid dates received:", dates);
              return;
            }

            setSelectedDates({
              startDate: start.toISOString(),
              endDate: end.toISOString(),
            });

            const days = calculateBillableDays(
              start.toISOString(),
              end.toISOString()
            );

            setBillableDays(days);
          }}
        />
      )}

      {!isTrailer && product && (
        <RelatedProducts currentProduct={product} />
      )}
    </>
  );
};

export default ProductPage;